/**
 * C2 citation-revocation production action SDK (Bundle 1 C2 action wiring).
 *
 * Thin shell-V1 POST helpers for the chip's acknowledge / escalate actions.
 * Mirrors api.ts's injectable-fetchImpl + explicit-apiBase pattern — no
 * hidden globals. The acting identity is derived SERVER-SIDE from the verified
 * session cookie (1b actor seam — `current_actor`); `credentials: 'include'`
 * sends that cookie. These endpoints take NO request body — the backend
 * `acknowledge_tombstone` / `escalate_tombstone` depend only on `current_actor`
 * (verified in repo: `app/api/v1/artifact_citations.py`). The former inert
 * client `actor` body field was removed here (T-FS-1) — it was ignored by the
 * backend and a client cannot supply/override the acting identity.
 *
 * Backend contracts (do NOT invent — verified in repo):
 *   POST /api/workspace/artifact_citations/{citation_id}/acknowledge
 *     body: (none) · actor = current_actor (session)
 *     → CitationResponse (full row with updated tombstone_acknowledged_at)
 *
 *   POST /api/workspace/artifact_citations/{citation_id}/escalate
 *     body: (none) · actor = current_actor (session)
 *     → CitationResponse (full row with updated tombstone_escalated_to_owner_at)
 *
 * Spec ref: `02_specs/05_artifact_lifecycle_and_citations_2026-05-31.md` §6, §9.
 *
 * The chip's `onAction` intent is consumed by a separate hook
 * (useCitationRevocationActions) that wires these helpers into React-Query
 * mutations with cache invalidation. This file is the pure fetch layer.
 */
import type {
  CitationBackendResponse,
  CitationRevocationApiOptions,
} from './api'

async function _postCitationAction(
  citationId: string,
  pathSuffix: 'acknowledge' | 'escalate',
  opts: CitationRevocationApiOptions,
): Promise<CitationBackendResponse> {
  const f = opts.fetchImpl ?? fetch
  const base = opts.apiBase.replace(/\/$/, '')
  const url = `${base}/artifact_citations/${encodeURIComponent(citationId)}/${pathSuffix}`
  const res = await f(url, {
    method: 'POST',
    // No body — the acting identity is the verified session cookie, not a
    // client-supplied field. `credentials: 'include'` sends that cookie.
    credentials: 'include',
  })
  if (!res.ok) {
    throw new Error(
      `postCitationAction ${pathSuffix} ${citationId}: HTTP ${res.status} ${res.statusText}`,
    )
  }
  return (await res.json()) as CitationBackendResponse
}

/**
 * POST /artifact_citations/{citation_id}/acknowledge.
 *
 * Records the tombstone-acknowledged state on the citation. Returns the full
 * updated CitationResponse so callers can update local caches optimistically.
 * Non-2xx throws (e.g. backend's CITATION_NOT_FOUND 404 + 409 invalid-state).
 */
export async function postAcknowledgeCitation(
  citationId: string,
  opts: CitationRevocationApiOptions,
): Promise<CitationBackendResponse> {
  return _postCitationAction(citationId, 'acknowledge', opts)
}

/**
 * POST /artifact_citations/{citation_id}/escalate.
 *
 * Records the escalation timestamp (spec §6: notifies the artifact owner).
 * Returns the full updated CitationResponse. Non-2xx throws.
 */
export async function postEscalateCitation(
  citationId: string,
  opts: CitationRevocationApiOptions,
): Promise<CitationBackendResponse> {
  return _postCitationAction(citationId, 'escalate', opts)
}
